import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserSettings, updateUserSettings, deleteUserSetting } from '../../api/userSettings';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import { reapplySettingsChange } from './reapplySettingsChange';

export {
  USER_SETTING_EVENT_NOTIFICATIONS,
  USER_SETTING_PREVIEW_FEATURES,
  USER_SETTING_CHART_PREFERENCES,
  USER_SETTING_SEMI_LIVE_CANDLES,
} from './userSettingKeys';

const normalizeValue = (value) => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true') { return true; }
    if (lower === 'false') { return false; }
  }
  return value;
};

const normalizeSettings = (settings = {}) => {
  const entries = Object.entries(settings).map(([key, value]) => [key, normalizeValue(value)]);
  return Object.fromEntries(entries);
};

const FIVE_MINUTES = 5 * 60 * 1000;

/** Which account a set of settings belongs to: accountId, as usePermissions and the account registry key it. */
export const accountKeyOf = (user) => user?.accountId ?? null;

// A failed read is retried this many times before the fetch rejects, but only for a
// network error or a 5xx: a 401/403 belongs to the auth flow, which logs out on its own.
export const FETCH_RETRY_DELAYS_MS = [1000, 3000];
const isRetryable = (error) => !error?.response || error.response.status >= 500;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const initialState = {
  settings: {},
  loading: false,
  loaded: false,
  lastFetched: null,
  error: null,
  // `loaded` means a fetch has FINISHED, successfully or not, and stays that way for the
  // pages that only need to stop showing a spinner. `synced` means `settings` really are
  // the server's, read for the account in `syncedAccount`. Writing back any part of a
  // settings map — chartPreferences above all, whose entries are sent as read — requires
  // synced, so a failed or stale read can never be saved over the real thing.
  synced: false,
  syncedAccount: null,
  // The settings as the server last returned them. `settings` can run ahead of it (the settings
  // page edits locally until Save), so this is what a save's change is measured against.
  serverSettings: {},
  currentRequestId: null,
  requestAccount: null,
  saving: false,
  saveError: null,
  deletingKey: null,
  deleteError: null,
};

export const fetchUserSettings = createAsyncThunk(
  'userSettings/fetch',
  async (arg, { rejectWithValue, getState, requestId }) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const settings = await getUserSettings();
        return settings || {};
      } catch (error) {
        const superseded = getState().userSettings.currentRequestId !== requestId;
        if (superseded || attempt >= FETCH_RETRY_DELAYS_MS.length || !isRetryable(error)) {
          return rejectWithValue(extractErrorMessage(error, 'Failed to load settings'));
        }
        await wait(FETCH_RETRY_DELAYS_MS[attempt]);
      }
    }
  },
  {
    // The account the read is made for, so its result is never credited to another one.
    getPendingMeta: (base, { getState }) => ({ account: accountKeyOf(getState().account?.user) }),
    condition: (arg, { getState }) => {
      if (arg?.force) return undefined;
      const { userSettings } = getState();
      if (userSettings.loaded && userSettings.lastFetched > Date.now() - FIVE_MINUTES) {
        return false;
      }
    },
  }
);

export const saveUserSettings = createAsyncThunk(
  'userSettings/save',
  async (settings, { rejectWithValue, fulfillWithValue, getState }) => {
    // The account the save is for travels on its outcome, so it is never credited to another.
    const { serverSettings: base, syncedAccount: account } = getState().userSettings;
    const fail = (error) => rejectWithValue(extractErrorMessage(error, 'Failed to save settings'), { account });
    try {
      const updated = await updateUserSettings(settings);
      return fulfillWithValue(updated || {}, { account });
    } catch (error) {
      if (error?.response?.status !== 409) return fail(error);
    }
    // 409: another write landed between the server's read and its write. Read again, re-apply
    // only what this save changed, and send that once — never the stale map as it was.
    try {
      const fresh = await getUserSettings();
      // The re-read goes out as whoever is signed in by then; only the save's own account may be written.
      const now = getState();
      if (!selectUserSettingsSynced(now) || now.userSettings.syncedAccount !== account) {
        return rejectWithValue('Settings changed while saving', { account });
      }
      const updated = await updateUserSettings(reapplySettingsChange({ fresh: fresh || {}, base, sent: settings }));
      return fulfillWithValue(updated || {}, { account });
    } catch (error) {
      return fail(error);
    }
  },
  {
    // Every caller sends a whole settings map built from the store, and the API stores each
    // top-level value in place of the server's (chartPreferences whole, until SPT #708 merges it
    // per symbol: a stale map still re-sends every symbol as it was read). Unless that map
    // was really read for this account, the save is not sent at all (DWLF-508).
    condition: (settings, { getState }) => selectUserSettingsSynced(getState()),
  }
);

export const removeUserSetting = createAsyncThunk(
  'userSettings/remove',
  async (settingKey, { rejectWithValue }) => {
    try {
      const updated = await deleteUserSetting(settingKey);
      return { settingKey, settings: updated || {} };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to delete setting'));
    }
  }
);

const isOwnSession = (state, action) => state.synced && action.meta.account === state.syncedAccount;

const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState,
  reducers: {
    setLocalSetting(state, action) {
      const { settingKey, value } = action.payload;
      state.settings = {
        ...state.settings,
        [settingKey]: normalizeValue(value),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserSettings.pending, (state, action) => {
        state.loading = true;
        state.loaded = false;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.requestAccount = action.meta.account ?? null;
      })
      // A response to a read that is no longer the current one — superseded, or begun before
      // a sign-out reset the store — is dropped: it may belong to another session.
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.loaded = true;
        state.lastFetched = Date.now();
        state.settings = normalizeSettings(action.payload);
        state.serverSettings = state.settings;
        state.synced = true;
        state.syncedAccount = state.requestAccount;
      })
      .addCase(fetchUserSettings.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.loaded = true;
        state.error = action.payload || action.error?.message || 'Failed to load settings';
      })

      .addCase(saveUserSettings.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      // A save is only sent while synced (its condition), so landing unsynced, or synced for
      // another account, means a sign-out happened while it was in flight: its outcome belongs
      // to the previous session.
      .addCase(saveUserSettings.fulfilled, (state, action) => {
        state.saving = false;
        if (!isOwnSession(state, action)) return;
        state.loaded = true;
        state.settings = normalizeSettings(action.payload);
        state.serverSettings = state.settings;
      })
      .addCase(saveUserSettings.rejected, (state, action) => {
        state.saving = false;
        if (!isOwnSession(state, action)) return;
        state.saveError = action.payload || action.error?.message || 'Failed to save settings';
      })

      .addCase(removeUserSetting.pending, (state, action) => {
        state.deletingKey = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(removeUserSetting.fulfilled, (state, action) => {
        const { settingKey, settings: updatedSettings } = action.payload || {};
        if (updatedSettings) {
          state.settings = normalizeSettings(updatedSettings);
          state.serverSettings = state.settings;
        } else if (settingKey) {
          const updated = { ...state.settings };
          delete updated[settingKey];
          state.settings = updated;
        }
        state.loaded = true;
        state.deletingKey = null;
      })
      .addCase(removeUserSetting.rejected, (state, action) => {
        state.deleteError = action.payload || action.error?.message || 'Failed to delete setting';
        state.deletingKey = null;
      });
  },
});

export const { setLocalSetting } = userSettingsSlice.actions;

/** True only when `settings` were read from the server for the account signed in now. */
export function selectUserSettingsSynced(state) {
  return Boolean(state.userSettings?.synced) &&
    state.userSettings.syncedAccount === accountKeyOf(state.account?.user);
}
export default userSettingsSlice.reducer;
